import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Applications from "@/pages/Applications";
import Billing from "@/pages/Billing";
import Complaints from "@/pages/Complaints";
import Payments from "@/pages/Payments";
import Staff from "@/pages/Staff";
import AuditLogs from "@/pages/AuditLogs";
import AiAssistant from "@/pages/AiAssistant";
import RentAgreements from "@/pages/RentAgreements";
import POS from "@/pages/POS";
import GoLive from "@/pages/GoLive";
import Marketing from "@/pages/Marketing";
import Reports from "@/pages/Reports";
import DataImport from "@/pages/DataImport";
import Expenses from "@/pages/Expenses";
import Branches from "@/pages/Branches";
import ServiceRates from "@/pages/ServiceRates";
import Collections from "@/pages/Collections";
import GovFinance from "@/pages/GovFinance";
import HR from "@/pages/HR";
import PrintCenter from "@/pages/PrintCenter";
import PaymentCenter from "@/pages/PaymentCenter";
import GovPortals from "@/pages/GovPortals";
import WhatsAppCenter from "@/pages/WhatsAppCenter";
import PhotoScanStudio from "@/pages/PhotoScanStudio";
import AIPhotoEnhancer from "@/pages/AIPhotoEnhancer";
import PdfStudio from "@/pages/PdfStudio";
import TrackingDashboard from "@/pages/TrackingDashboard";
import Settings from "@/pages/Settings";
import {
  BusinessProfile, TaxesSettings, SmsSettings, EmailSettings,
  WebsiteSettings, SeoSettings, SecuritySettings, GeneralSettings,
} from "@/pages/settings/SettingsPages";

// DigitalSetu public site
import PublicLayout   from "@/pages/public/PublicLayout";
import PublicHome     from "@/pages/public/PublicHome";
import PublicServices from "@/pages/public/PublicServices";
import PublicTrack    from "@/pages/public/PublicTrack";
import PublicApply    from "@/pages/public/PublicApply";
import PublicPricing  from "@/pages/public/PublicPricing";
import PublicBranches from "@/pages/public/PublicBranches";
import PublicAbout    from "@/pages/public/PublicAbout";
import PublicContact  from "@/pages/public/PublicContact";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const admin = ["admin", "super_admin", "branch_admin"].includes(user.role);
  if (!admin) return <Navigate to="/dashboard" replace />;
  return children;
}

// If a signed-in user hits "/", send them to the app dashboard;
// otherwise show the DigitalSetu public homepage.
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return <PublicHome />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* Public marketing site */}
            <Route element={<PublicLayout />}>
              <Route path="/"          element={<RootRoute />} />
              <Route path="/services"  element={<PublicServices />} />
              <Route path="/apply"     element={<PublicApply />} />
              <Route path="/track"     element={<PublicTrack />} />
              <Route path="/pricing"   element={<PublicPricing />} />
              <Route path="/branches"  element={<PublicBranches />} />
              <Route path="/about"     element={<PublicAbout />} />
              <Route path="/contact"   element={<PublicContact />} />
            </Route>

            {/* Authenticated ERP */}
            <Route element={<Protected><AppLayout /></Protected>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/applications" element={<Applications />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/payments" element={<Payments />} />
              <Route path="/complaints" element={<Complaints />} />
              <Route path="/rent-agreements" element={<RentAgreements />} />
              <Route path="/pos" element={<POS />} />
              <Route path="/marketing" element={<Marketing />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/branches-admin" element={<Branches />} />
              <Route path="/service-rates" element={<ServiceRates />} />
              <Route path="/collections" element={<Collections />} />
              <Route path="/gov-finance" element={<GovFinance />} />
              <Route path="/hr" element={<HR />} />
              <Route path="/print-center" element={<PrintCenter />} />
              <Route path="/payment-center" element={<PaymentCenter />} />
              <Route path="/gov-portals" element={<GovPortals />} />
              <Route path="/whatsapp-center" element={<WhatsAppCenter />} />
              <Route path="/photo-studio" element={<PhotoScanStudio />} />
              <Route path="/photo-enhancer" element={<AIPhotoEnhancer />} />
              <Route path="/pdf-studio" element={<PdfStudio />} />
              <Route path="/tracking" element={<TrackingDashboard />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/business" element={<BusinessProfile />} />
              <Route path="/settings/taxes"    element={<TaxesSettings />} />
              <Route path="/settings/sms"      element={<SmsSettings />} />
              <Route path="/settings/email"    element={<EmailSettings />} />
              <Route path="/settings/website"  element={<WebsiteSettings />} />
              <Route path="/settings/seo"      element={<SeoSettings />} />
              <Route path="/settings/security" element={<SecuritySettings />} />
              <Route path="/settings/general"  element={<GeneralSettings />} />
              <Route path="/data-import" element={<DataImport />} />
              <Route path="/go-live" element={<GoLive />} />
              <Route path="/ai-drafts" element={<AiAssistant />} />
              <Route path="/staff" element={<AdminOnly><Staff /></AdminOnly>} />
              <Route path="/audit-logs" element={<AdminOnly><AuditLogs /></AdminOnly>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
